import { CompactEncrypt, exportJWK, generateKeyPair, importJWK } from 'jose';
import { getBiometricsConfig } from './config';
let temporaryJweKeyPromise;
export const formatByteSize = bytes => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  return `${(bytes / 1024).toFixed(2)} KB`;
};
export const dataUrlToBytes = dataUrl => {
  const base64Data = dataUrl.split(',')[1] || '';
  const binaryData = atob(base64Data);
  const bytes = new Uint8Array(binaryData.length);
  for (let index = 0; index < binaryData.length; index += 1) {
    bytes[index] = binaryData.charCodeAt(index);
  }
  return bytes;
};
export const base64UrlByteLength = value => Math.floor(value.length * 3 / 4);
export const getTemporaryJweKey = async algorithm => {
  temporaryJweKeyPromise ||= generateKeyPair(algorithm, {
    extractable: true
  }).then(async ({
    publicKey
  }) => ({
    key: publicKey,
    keyId: 'local-temporary-jwe-key',
    publicJwk: await exportJWK(publicKey)
  }));
  return temporaryJweKeyPromise;
};
export const encryptFaceImage = async photoData => {
  const {
    jweAlgorithm: JWE_ALGORITHM,
    jweEncryption: JWE_ENCRYPTION,
    jweKid,
    jwePublicJwk: publicJwkJson,
    isDevelopment
  } = getBiometricsConfig();
  const totalStartTime = performance.now();
  const imageBytes = dataUrlToBytes(photoData);
  const keyImportStartTime = performance.now();
  let publicKey;
  let publicJwk;
  let keyId;
  let usingTemporaryKey = false;
  if (publicJwkJson) {
    publicJwk = JSON.parse(publicJwkJson);
    publicKey = await importJWK(publicJwk, JWE_ALGORITHM);
    keyId = jweKid || publicJwk.kid;
  } else if (isDevelopment) {
    ({
      key: publicKey,
      keyId,
      publicJwk
    } = await getTemporaryJweKey(JWE_ALGORITHM));
    usingTemporaryKey = true;
    console.warn('[Biometrics] Using a temporary browser-generated JWE key for local testing. The biometric service cannot decrypt this payload.');
  } else {
    console.warn('[Biometrics] JWE encryption skipped: biometricsJwePublicJwk is not configured.');
    return null;
  }
  const keyImportDurationMs = performance.now() - keyImportStartTime;
  const encryptionStartTime = performance.now();
  const jwe = await new CompactEncrypt(imageBytes).setProtectedHeader({
    alg: JWE_ALGORITHM,
    enc: JWE_ENCRYPTION,
    kid: keyId,
    cty: 'image/jpeg'
  }).encrypt(publicKey);
  const encryptionDurationMs = performance.now() - encryptionStartTime;
  const totalDurationMs = performance.now() - totalStartTime;
  const [protectedHeader, encryptedKey, initializationVector, ciphertext, authenticationTag] = jwe.split('.');
  console.groupCollapsed('[Biometrics] JWE encryption metrics');
  console.table({
    algorithm: JWE_ALGORITHM,
    encryption: JWE_ENCRYPTION,
    keyId: keyId || 'not supplied',
    usingTemporaryKey,
    recipientKeyType: publicJwk.kty,
    inputImageBytes: imageBytes.length,
    inputImageReadableSize: formatByteSize(imageBytes.length),
    keyImportDurationMs: Number(keyImportDurationMs.toFixed(2)),
    encryptionDurationMs: Number(encryptionDurationMs.toFixed(2)),
    totalDurationMs: Number(totalDurationMs.toFixed(2)),
    compactJweParts: [protectedHeader, encryptedKey, initializationVector, ciphertext, authenticationTag].length,
    payloadBytes: new TextEncoder().encode(jwe).length,
    payloadReadableSize: formatByteSize(new TextEncoder().encode(jwe).length),
    payloadCharacters: jwe.length,
    protectedHeaderBytes: base64UrlByteLength(protectedHeader),
    encryptedKeyBytes: base64UrlByteLength(encryptedKey),
    initializationVectorBytes: base64UrlByteLength(initializationVector),
    ciphertextBytes: base64UrlByteLength(ciphertext),
    authenticationTagBytes: base64UrlByteLength(authenticationTag)
  });
  if (isDevelopment) {
    console.log('Compact JWE payload (development only):', jwe);
  }
  console.groupEnd();
  return {
    jwe,
    encryptionDurationMs,
    totalDurationMs,
    payloadBytes: new TextEncoder().encode(jwe).length,
    payloadReadableSize: formatByteSize(new TextEncoder().encode(jwe).length),
    payloadCharacters: jwe.length
  };
};
export const logCaptureDiagnostics = ({
  canvas,
  photoData,
  video
}) => {
  var _metadata$match, _video$srcObject, _navigator$userAgentD, _track$getCapabilitie;
  const [metadata, base64Data = ''] = photoData.split(',');
  const mimeType = ((_metadata$match = metadata.match(/data:(.*?);base64/)) === null || _metadata$match === void 0 ? void 0 : _metadata$match[1]) || 'unknown';
  const imageBytes = Math.floor(base64Data.length * 3 / 4) - (base64Data.endsWith('==') ? 2 : base64Data.endsWith('=') ? 1 : 0);
  const track = (_video$srcObject = video.srcObject) === null || _video$srcObject === void 0 ? void 0 : _video$srcObject.getVideoTracks()[0];
  console.groupCollapsed('[Biometrics] Captured face image metadata');
  console.table({
    mimeType,
    byteSize: imageBytes,
    readableSize: formatByteSize(imageBytes),
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
    sourceVideoWidth: video.videoWidth,
    sourceVideoHeight: video.videoHeight,
    encodedDataUrlCharacters: photoData.length
  });
  console.log('Device:', {
    userAgent: navigator.userAgent,
    platform: ((_navigator$userAgentD = navigator.userAgentData) === null || _navigator$userAgentD === void 0 ? void 0 : _navigator$userAgentD.platform) || navigator.platform
  });
  console.log('Camera track settings:', track === null || track === void 0 ? void 0 : track.getSettings());
  console.log('Camera track capabilities:', track === null || track === void 0 || (_track$getCapabilitie = track.getCapabilities) === null || _track$getCapabilitie === void 0 ? void 0 : _track$getCapabilitie.call(track));
  console.info('The image data URL is intentionally not logged because it contains biometric data. Inspect the on-screen preview or application state only in an approved environment.');
  console.groupEnd();
};